import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, ShieldCheck, Server, Zap, Globe, Terminal, ArrowRight, Check, Inbox as InboxIcon, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const SNIPPETS = {
  curl: `curl https://smtp.relaymx.io/api/send \\
  -H "X-API-Key: rmx_live_••••" \\
  -H "Content-Type: application/json" \\
  -d '{
    "from_addr": "you@yourcompany.com",
    "to": ["user@example.com"],
    "subject": "Welcome aboard",
    "html": "<h1>Hello 👋</h1>"
  }'`,
  smtp: `import smtplib
from email.message import EmailMessage

msg = EmailMessage()
msg["From"] = "you@yourcompany.com"
msg["To"] = "user@example.com"
msg["Subject"] = "Welcome aboard"
msg.set_content("Hello from RelayMX")

s = smtplib.SMTP("smtp.relaymx.io", 587)
s.starttls()
s.login("USERNAME", "PASSWORD")
s.send_message(msg)`,
  node: `import { RelayMX } from "relaymx";

const client = new RelayMX("rmx_live_••••");

await client.send({
  from: "you@yourcompany.com",
  to: ["user@example.com"],
  subject: "Welcome aboard",
  html: "<h1>Hello 👋</h1>",
});`,
};

function Nav() {
  const { user } = useAuth();
  return (
    <header className="sticky top-0 z-50 border-b border-slate-800/80 bg-[#0B0F17]/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500 text-[#04120c]"><Mail className="h-5 w-5" /></div>
          <span className="display text-lg font-bold tracking-tight">RelayMX</span>
        </Link>
        <nav className="hidden items-center gap-8 text-sm text-slate-400 md:flex">
          <a href="#features" className="transition-colors hover:text-white">Features</a>
          <a href="#how" className="transition-colors hover:text-white">How it works</a>
          <a href="#pricing" className="transition-colors hover:text-white">Pricing</a>
        </nav>
        <div className="flex items-center gap-3">
          {user ? (
            <Link to="/app" data-testid="nav-dashboard-link" className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-[#04120c] transition-colors hover:bg-emerald-400">Dashboard</Link>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-button" className="text-sm font-medium text-slate-300 transition-colors hover:text-white">Sign in</Link>
              <Link to="/signup" data-testid="nav-signup-button" className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-[#04120c] transition-colors hover:bg-emerald-400">Start free</Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

export default function Landing() {
  const [tab, setTab] = useState("curl");
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#0B0F17] text-white">
      <Nav />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="grid-bg absolute inset-0 opacity-60" />
        <div className="absolute -top-40 left-1/4 h-96 w-96 rounded-full bg-emerald-500/10 blur-[120px]" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 py-20 lg:grid-cols-2 lg:py-28">
          <div className="fade-up">
            <span className="mono inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/5 px-3 py-1 text-xs uppercase tracking-[0.15em] text-emerald-400">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-dot" /> SMTP infrastructure
            </span>
            <h1 className="display mt-6 text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              Your own mail server,<br /><span className="text-emerald-400">on your domain.</span>
            </h1>
            <p className="mt-6 max-w-lg text-lg leading-relaxed text-slate-400">
              Send and receive email from <span className="text-slate-200">@yourcompany.com</span> in minutes. Verify your domain with SPF, DKIM & DMARC, grab SMTP credentials or an API key, and ship.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button data-testid="hero-get-started" onClick={() => navigate("/signup")} className="group inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-6 py-3 font-semibold text-[#04120c] transition-all hover:bg-emerald-400 hover:shadow-[0_0_30px_-5px_rgba(16,185,129,0.6)]">
                Start sending <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              <a href="#how" className="inline-flex items-center gap-2 rounded-xl border border-slate-700 px-6 py-3 font-medium text-slate-300 transition-colors hover:border-slate-500 hover:text-white">See how it works</a>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-500">
              <span className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-400" /> SPF · DKIM · DMARC</span>
              <span className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-400" /> SMTP + REST API</span>
              <span className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-400" /> Inbound mailboxes</span>
            </div>
          </div>

          {/* code card */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-950/80 shadow-2xl">
            <div className="flex items-center gap-2 border-b border-slate-800 px-4 py-3">
              <span className="h-3 w-3 rounded-full bg-red-500/70" />
              <span className="h-3 w-3 rounded-full bg-amber-500/70" />
              <span className="h-3 w-3 rounded-full bg-emerald-500/70" />
              <div className="ml-4 flex gap-1">
                {Object.keys(SNIPPETS).map((k) => (
                  <button key={k} onClick={() => setTab(k)} data-testid={`snippet-tab-${k}`}
                    className={`mono rounded-md px-3 py-1 text-xs capitalize transition-colors ${tab === k ? "bg-slate-800 text-emerald-400" : "text-slate-500 hover:text-slate-300"}`}>
                    {k}
                  </button>
                ))}
              </div>
            </div>
            <pre className="mono overflow-x-auto p-5 text-xs leading-relaxed text-slate-300"><code>{SNIPPETS[tab]}</code></pre>
            <div className="flex items-center justify-between border-t border-slate-800 bg-emerald-500/5 px-5 py-3">
              <span className="mono text-xs text-emerald-400">● 250 Message accepted for delivery</span>
              <span className="mono text-xs text-slate-500">~120ms</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="mx-auto max-w-7xl px-6 py-20">
        <h2 className="display text-3xl font-bold tracking-tight sm:text-4xl">Everything a mail server should be</h2>
        <p className="mt-3 max-w-2xl text-slate-400">Built for engineering teams who want deliverability without running Postfix at 3am.</p>
        <div className="mt-12 grid gap-px overflow-hidden rounded-2xl border border-slate-800 bg-slate-800 md:grid-cols-3">
          {[
            { icon: Globe, t: "Bring your domain", d: "Add any domain and get copy-paste SPF, DKIM and DMARC records with live verification." },
            { icon: Zap, t: "SMTP + REST API", d: "Plug credentials into any app or fire a single HTTPS request. Whatever fits your stack." },
            { icon: ShieldCheck, t: "DKIM signing", d: "Every message is signed with a 2048-bit DKIM key generated per domain. Inbox, not spam." },
            { icon: InboxIcon, t: "Inbound mailboxes", d: "Receive replies on your domain and read them right inside the dashboard." },
            { icon: Server, t: "Delivery logs", d: "Searchable, filterable logs with SMTP responses and full payload inspection." },
            { icon: KeyRound, t: "Scoped API keys", d: "Issue and revoke keys per app. Rotate instantly without touching your domain." },
          ].map((f, i) => (
            <div key={i} className="group bg-[#0B0F17] p-8 transition-colors hover:bg-slate-900/60">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-700 bg-slate-800/60 text-emerald-400 transition-all group-hover:border-emerald-500/40">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="display mt-5 text-lg font-semibold">{f.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-y border-slate-800 bg-slate-900/30">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <h2 className="display text-3xl font-bold tracking-tight sm:text-4xl">Live in four steps</h2>
          <div className="mt-12 grid gap-8 md:grid-cols-4">
            {[
              { n: "01", t: "Add your domain", d: "Enter mail.yourcompany.com and we generate your DKIM key." },
              { n: "02", t: "Set DNS records", d: "Copy SPF, DKIM, DMARC to your DNS provider." },
              { n: "03", t: "Verify", d: "One click checks your records and flips you to verified." },
              { n: "04", t: "Send", d: "Use SMTP creds or an API key and start delivering." },
            ].map((s) => (
              <div key={s.n} className="relative rounded-xl border border-slate-800 bg-[#0B0F17] p-6">
                <span className="mono text-2xl font-bold text-emerald-400/40">{s.n}</span>
                <h3 className="display mt-3 text-lg font-semibold">{s.t}</h3>
                <p className="mt-2 text-sm text-slate-400">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="mx-auto max-w-7xl px-6 py-20">
        <h2 className="display text-center text-3xl font-bold tracking-tight sm:text-4xl">Simple, scalable pricing</h2>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {[
            { t: "Developer", p: "Free", d: "For side projects", f: ["3,000 emails/mo", "1 domain", "SMTP + API", "Community support"], hot: false },
            { t: "Growth", p: "$29", d: "For growing teams", f: ["100,000 emails/mo", "10 domains", "Inbound mailboxes", "Priority support"], hot: true },
            { t: "Enterprise", p: "Custom", d: "Dedicated IP & SLA", f: ["Unlimited volume", "Unlimited domains", "Dedicated IP warm-up", "99.99% SLA"], hot: false },
          ].map((tier) => (
            <div key={tier.t} className={`relative rounded-2xl border p-8 ${tier.hot ? "border-emerald-500/50 bg-emerald-500/5 glow-emerald" : "border-slate-800 bg-[#0B0F17]"}`}>
              {tier.hot && <span className="mono absolute -top-3 left-8 rounded-full bg-emerald-500 px-3 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#04120c]">Popular</span>}
              <h3 className="display text-xl font-semibold">{tier.t}</h3>
              <p className="mt-1 text-sm text-slate-400">{tier.d}</p>
              <div className="mt-5 flex items-end gap-1"><span className="display text-4xl font-bold">{tier.p}</span>{tier.p !== "Custom" && <span className="mb-1 text-slate-500">/mo</span>}</div>
              <ul className="mt-6 space-y-3 text-sm">
                {tier.f.map((x) => <li key={x} className="flex items-center gap-2 text-slate-300"><Check className="h-4 w-4 text-emerald-400" />{x}</li>)}
              </ul>
              <button onClick={() => navigate("/signup")} className={`mt-8 w-full rounded-xl py-2.5 font-semibold transition-colors ${tier.hot ? "bg-emerald-500 text-[#04120c] hover:bg-emerald-400" : "border border-slate-700 text-white hover:border-slate-500"}`}>Get started</button>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-slate-800">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-6 py-8 text-sm text-slate-500 sm:flex-row">
          <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-emerald-400" /> RelayMX — SMTP for your domain</div>
          <span className="mono text-xs">smtp.relaymx.io · built for developers</span>
        </div>
      </footer>
    </div>
  );
}
